import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { ArrowLeft, CheckCircle, XCircle, Clock, Ban, Paperclip, Upload, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS_CONFIG = {
  pendiente: { label: "Pendiente", color: "bg-yellow-100 text-yellow-700", Icon: Clock },
  aprobada: { label: "Aprobada", color: "bg-green-100 text-green-700", Icon: CheckCircle },
  rechazada: { label: "Rechazada", color: "bg-red-100 text-red-700", Icon: XCircle },
  cancelada: { label: "Cancelada", color: "bg-slate-100 text-slate-500", Icon: Ban },
  cerrada: { label: "Cerrada", color: "bg-slate-100 text-slate-600", Icon: CheckCircle },
};

const TYPE_LABELS = {
  compra: "Requisición de compra", gasto: "Gasto", reembolso: "Reembolso",
  anticipo: "Anticipo", vacante: "Vacante",
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "bg-slate-100 text-slate-600", Icon: Clock };
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>
      <Icon className="w-3.5 h-3.5" />
      {status === "vacante_aprobada" ? "Vacante autorizada" : cfg.label}
    </span>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function OpsSolicitudDetalle() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [acting, setActing] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/ops/requests/${id}`);
      setReq(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la solicitud");
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const canDecide = req && req.status === "pendiente" && req.current_step != null;

  const decide = async (action) => {
    if (action === "reject" && !comment.trim()) { toast.error("El rechazo requiere un comentario"); return; }
    setActing(true);
    try {
      await api.post(`/ops/requests/${id}/decision`, { action, comment: comment || undefined });
      toast.success(action === "approve" ? "Solicitud aprobada" : "Solicitud rechazada");
      setComment("");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo procesar la decisión");
    } finally { setActing(false); }
  };

  const closeManual = async () => {
    if (!comment.trim() || comment.trim().length < 5) { toast.error("Indica cómo/cuándo se pagó (mínimo 5 caracteres)"); return; }
    setActing(true);
    try {
      await api.post(`/ops/requests/${id}/close`, { comment });
      toast.success("Solicitud cerrada");
      setComment("");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cerrar");
    } finally { setActing(false); }
  };

  const cancel = async () => {
    if (!comment.trim() || comment.trim().length < 5) { toast.error("Indica el motivo de la cancelación (mínimo 5 caracteres)"); return; }
    setActing(true);
    try {
      await api.post(`/ops/requests/${id}/cancel`, { reason: comment });
      toast.success("Solicitud cancelada");
      setComment("");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cancelar");
    } finally { setActing(false); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const b64 = await fileToBase64(file);
      await api.post(`/ops/requests/${id}/attachments`, { filename: file.name, content_base64: b64 });
      toast.success("Adjunto subido");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir el archivo");
    } finally { setUploading(false); e.target.value = ""; }
  };

  if (loading) return <div className="flex justify-center py-16"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!req) return null;

  const isOwner = req.requester_id === user?.id;
  const canClose = ["reembolso", "anticipo"].includes(req.type) && req.status === "aprobada" && ["corporate_admin", "treasury"].includes(user?.role);
  const canCancel = isOwner && req.status === "pendiente";

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="ops-solicitud-detalle-page">
      <button onClick={() => navigate("/ops/solicitudes")} className="text-sm font-semibold text-slate-500 hover:text-slate-900 flex items-center gap-1.5">
        <ArrowLeft className="w-4 h-4" /> Volver al buzón
      </button>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">{TYPE_LABELS[req.type] || req.type}</h1>
            <p className="text-sm text-slate-500">{req.requester_name} · {new Date(req.created_at).toLocaleDateString("es-MX")}</p>
          </div>
          <StatusBadge status={req.type === "vacante" && req.status === "aprobada" ? "vacante_aprobada" : req.status} />
        </div>

        {req.type === "vacante" && req.status === "aprobada" && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            El pipeline de reclutamiento todavía no existe en el sistema — esta vacante queda autorizada aquí.
          </div>
        )}
        {req.needs_rule_configuration && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5" data-testid="alerta-sin-regla">
            Esta solicitud no tiene ninguna regla de aprobación configurada. Un administrador debe crearla en Reglas de aprobación.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Monto</div>
            <div className="font-bold text-lg">${(req.monto || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} {req.currency}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Área</div>
            <div className="font-semibold">{req.area || "—"}</div>
          </div>
        </div>

        <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1" data-testid="solicitud-payload">
          {Object.entries(req.payload || {}).map(([k, v]) => v != null && v !== "" ? (
            <div key={k} className="flex justify-between"><span className="text-slate-500">{k}</span><span className="font-semibold">{String(v)}</span></div>
          ) : null)}
          {req.notes && <div className="pt-1 border-t border-slate-200 mt-1"><span className="text-slate-500">Notas: </span>{req.notes}</div>}
        </div>

        {req.approvals?.length > 0 && (
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Pasos de aprobación</div>
            <div className="space-y-1.5">
              {req.approvals.map((a, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid={`approval-step-${idx}`}>
                  <span>Paso {a.orden} · {a.approver_kind === "rol" ? a.value : a.approver_kind === "area" ? `Área: ${a.value}` : "Jefe directo"}</span>
                  <StatusBadge status={a.status} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Adjuntos</div>
          <div className="space-y-1.5">
            {(req.attachments || []).map((a) => (
              <a key={a.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/ops/requests/${req.id}/attachments/${a.id}`} target="_blank" rel="noreferrer"
                className="block text-sm text-indigo-600 hover:underline" data-testid={`attachment-${a.id}`}>{a.filename}</a>
            ))}
            {(!req.attachments || req.attachments.length === 0) && <p className="text-sm text-slate-400">Sin adjuntos</p>}
          </div>
          {!["cerrada", "rechazada", "cancelada"].includes(req.status) && (
            <label className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 cursor-pointer hover:bg-slate-50" data-testid="btn-subir-adjunto">
              <Upload className="w-3.5 h-3.5" /> {uploading ? "Subiendo..." : "Adjuntar archivo"}
              <input type="file" className="hidden" onChange={uploadFile} disabled={uploading} />
            </label>
          )}
        </div>

        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Historial</div>
          <div className="space-y-1.5 text-sm">
            {(req.historial || []).map((h, idx) => (
              <div key={idx} className="flex justify-between text-slate-600 border-b border-slate-100 pb-1.5" data-testid={`historial-item-${idx}`}>
                <span>{h.by_name || h.by}: {h.action}{h.comment ? ` — "${h.comment}"` : ""}</span>
                <span className="text-xs text-slate-400">{new Date(h.at).toLocaleString("es-MX")}</span>
              </div>
            ))}
          </div>
        </div>

        {(canDecide || canClose || canCancel) && (
          <div className="pt-3 border-t border-slate-200 space-y-2">
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Comentario (obligatorio para rechazar/cerrar/cancelar)"
              data-testid="input-comentario-decision" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" rows={2} />
            <div className="flex gap-2">
              {canDecide && (
                <>
                  <button onClick={() => decide("approve")} disabled={acting} data-testid="btn-aprobar-solicitud"
                    className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold text-sm hover:bg-green-700 disabled:opacity-50">
                    <CheckCircle className="w-4 h-4" /> Aprobar
                  </button>
                  <button onClick={() => decide("reject")} disabled={acting} data-testid="btn-rechazar-solicitud"
                    className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-lg font-semibold text-sm hover:bg-red-700 disabled:opacity-50">
                    <XCircle className="w-4 h-4" /> Rechazar
                  </button>
                </>
              )}
              {canClose && (
                <button onClick={closeManual} disabled={acting} data-testid="btn-cerrar-solicitud"
                  className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-slate-800 text-white rounded-lg font-semibold text-sm hover:bg-slate-900 disabled:opacity-50">
                  Marcar como pagada/cerrada
                </button>
              )}
              {canCancel && (
                <button onClick={cancel} disabled={acting} data-testid="btn-cancelar-solicitud"
                  className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-semibold text-sm hover:bg-slate-50 disabled:opacity-50">
                  Cancelar solicitud
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
